/**
 * CSV for the FigJam import (backlog §17): a small RFC 4180 reader and writer, no dependency. Everything is plain text;
 * the limits are checked while reading so a huge or hostile file never builds a huge result.
 */
export const CSV_LIMITS = { bytes: 5 * 1024 * 1024, rows: 2000, cols: 50, cell: 5000 } as const;

export type CsvDelimiter = ',' | ';' | '\t';
export type CsvEncoding = 'utf-8' | 'windows-1252';
export type ParsedCsv = { rows: string[][]; width: number; truncatedCells: number; error: null | 'too_many_rows' | 'too_many_cols' };

const DELIMITERS: readonly CsvDelimiter[] = [',', ';', '\t'];
/** Control characters other than tab and line feed (a carriage return inside a cell becomes a line feed first). */
const isControl = (c: number) => (c < 32 && c !== 9 && c !== 10) || c === 127;
function stripControl(s: string): string {
  for (let i = 0; i < s.length; i++) {
    if (isControl(s.charCodeAt(i))) return [...s].filter((ch) => !isControl(ch.charCodeAt(0))).join('');
  }
  return s;
}

/**
 * Bytes to text: strict UTF-8 unless the person chose Windows-1252 (never a silent fallback); the BOM is dropped.
 */
export function decodeCsv(
  bytes: Uint8Array,
  encoding?: CsvEncoding,
): { ok: true; text: string; encoding: CsvEncoding } | { ok: false; reason: 'not_utf8' | 'too_large' } {
  if (bytes.byteLength > CSV_LIMITS.bytes) return { ok: false, reason: 'too_large' };
  if (encoding === 'windows-1252') return { ok: true, text: stripBom(new TextDecoder('windows-1252').decode(bytes)), encoding };
  try {
    return { ok: true, text: stripBom(new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes)), encoding: 'utf-8' };
  } catch {
    return { ok: false, reason: 'not_utf8' };
  }
}

const stripBom = (s: string) => (s.charCodeAt(0) === 0xfeff ? s.slice(1) : s);

/** The delimiter found the same number of times (outside quotes) on most of the first 20 lines; a comma otherwise. */
export function detectDelimiter(text: string): CsvDelimiter {
  const lines: Record<CsvDelimiter, number>[] = [];
  let cur = { ',': 0, ';': 0, '\t': 0 };
  let quoted = false;
  let used = false;
  for (let i = 0; i < text.length && lines.length < 20; i++) {
    const ch = text[i]!;
    if (ch === '"') quoted = !quoted;
    else if (!quoted && (ch === '\n' || ch === '\r')) {
      if (used) lines.push(cur);
      cur = { ',': 0, ';': 0, '\t': 0 };
      used = false;
      continue;
    } else if (!quoted && (ch === ',' || ch === ';' || ch === '\t')) cur[ch]++;
    used = true;
  }
  if (used && lines.length < 20) lines.push(cur);
  let best: CsvDelimiter = ',';
  let bestScore = [0, 0];
  for (const d of DELIMITERS) {
    const first = lines[0]?.[d] ?? 0;
    if (first === 0) continue;
    const score = [lines.filter((l) => l[d] === first).length, first];
    if (score[0]! > bestScore[0]! || (score[0] === bestScore[0] && score[1]! > bestScore[1]!)) {
      best = d;
      bestScore = score;
    }
  }
  return best;
}

/** Reads `text` (never throws): rows padded to the widest one, control characters removed, cells cut at the limit. */
export function parseCsv(text: string, delimiter: CsvDelimiter): ParsedCsv {
  const rows: string[][] = [];
  let row: string[] = [];
  let truncatedCells = 0;
  let width = 0;
  let error: ParsedCsv['error'] = null;
  const sep = delimiter.charCodeAt(0);
  const n = text.length;
  let i = 0;
  const push = (raw: string, quoted: boolean) => {
    let cell = quoted ? raw.replace(/\r\n?/g, '\n') : raw;
    cell = stripControl(cell);
    if (cell.length > CSV_LIMITS.cell) {
      cell = cell.slice(0, CSV_LIMITS.cell);
      truncatedCells++;
    }
    row.push(cell);
  };
  const endRow = (): boolean => {
    if (row.length > CSV_LIMITS.cols) {
      error = 'too_many_cols';
      return false;
    }
    if (rows.length === CSV_LIMITS.rows) {
      error = 'too_many_rows';
      return false;
    }
    width = Math.max(width, row.length);
    rows.push(row);
    row = [];
    return true;
  };
  while (i < n) {
    let cell: string;
    let quoted = false;
    if (text.charCodeAt(i) === 34) {
      quoted = true;
      const parts: string[] = [];
      let start = ++i;
      for (;;) {
        const q = text.indexOf('"', i);
        if (q === -1) {
          parts.push(text.slice(start));
          i = n;
          break;
        }
        if (text.charCodeAt(q + 1) === 34) {
          parts.push(text.slice(start, q + 1));
          i = start = q + 2;
          continue;
        }
        parts.push(text.slice(start, q));
        i = q + 1;
        break;
      }
      cell = parts.join('');
      // Anything between the closing quote and the next delimiter is kept, as lenient readers do.
      const tail = i;
      while (i < n && text.charCodeAt(i) !== sep && text.charCodeAt(i) !== 10 && text.charCodeAt(i) !== 13) i++;
      if (i > tail) cell += text.slice(tail, i);
    } else {
      const start = i;
      while (i < n && text.charCodeAt(i) !== sep && text.charCodeAt(i) !== 10 && text.charCodeAt(i) !== 13) i++;
      cell = text.slice(start, i);
    }
    push(cell, quoted);
    if (i >= n) break;
    const c = text.charCodeAt(i);
    if (c === sep) {
      i++;
      if (i >= n) push('', false); // "a," ends with an empty cell
      continue;
    }
    // Line end: \n, \r\n or a lone \r.
    i += c === 13 && text.charCodeAt(i + 1) === 10 ? 2 : 1;
    if (!endRow()) break;
  }
  if (error === null && row.length > 0) endRow();
  for (const r of rows) while (r.length < width) r.push('');
  return { rows, width, truncatedCells, error };
}

/** RFC 4180 text of `rows` (CRLF between rows, no final line end); `parseCsv` reads it back unchanged. */
export function serializeCsv(rows: readonly (readonly string[])[], delimiter: CsvDelimiter = ','): string {
  const quote = (c: string) => `"${c.replace(/"/g, '""')}"`;
  return rows
    .map((r) =>
      r.length === 1 && r[0] === ''
        ? '""' // a row of one empty cell would otherwise vanish
        : r.map((c) => (c.includes(delimiter) || /["\n\r]/.test(c) ? quote(c) : c)).join(delimiter),
    )
    .join('\r\n');
}

/** Cells a spreadsheet would run as a formula get a leading apostrophe (OWASP CSV injection). */
export function neutralizeFormulas(rows: readonly (readonly string[])[]): string[][] {
  return rows.map((r) => r.map((c) => (/^[=+\-@\t\r]/.test(c) ? `'${c}` : c)));
}

/** The file a person downloads: UTF-8 BOM (Excel), comma, formulas neutralized. */
export function exportCsv(rows: readonly (readonly string[])[]): string {
  return `\uFEFF${serializeCsv(neutralizeFormulas(rows), ',')}`;
}
